import { mobileEventBus$ } from 'AppRoot'
import { Message, MsgSource, MsgTypes, sendAndAwaitReply } from '../messaging/messaging'

declare global {
    interface Window {
        KNOVIGATOR_IS_MOBILE?: boolean
        KNOVIGATOR_NATIVE_CAPS?: {
            nativeShare?: boolean
            nativeShareUrl?: boolean
            clearCreds?: boolean
            versionInfo?: boolean
            cameraPermissionGate?: boolean
            videoCaptureInput?: boolean
            keyboardState?: boolean
            keyboardOpening?: boolean
            webAuthnPasskeys?: boolean
            operationDiagnostics?: number
        }
        ReactNativeWebView?: {
            postMessage: (message: string) => void
        }
    }
}

export type NativeCapability =
    | 'nativeShare'
    | 'nativeShareUrl'
    | 'clearCreds'
    | 'versionInfo'
    | 'cameraPermissionGate'
    | 'videoCaptureInput'
    | 'keyboardState'
    | 'keyboardOpening'

export interface NativeSharePayload {
    title?: string
    text?: string
    url?: string
    fileBase64?: string
    fileUrl?: string
    fileName?: string
    mimeType?: string
}

export interface NativeShareResult {
    action?: string
    activityType?: string | null
    dismissed?: boolean
    error?: string
}

export function isRunningInExpoWebView(): boolean {
    return (
        typeof window !== 'undefined' &&
        !!window.KNOVIGATOR_IS_MOBILE &&
        !!window.ReactNativeWebView
    )
}

export function hasNativeCapability(capability: NativeCapability): boolean {
    return isRunningInExpoWebView() && window.KNOVIGATOR_NATIVE_CAPS?.[capability] === true
}

async function sendNativeShare(
    payload: NativeSharePayload,
    options: { timeoutMs?: number } = {},
): Promise<NativeShareResult> {
    const message = new Message(JSON.stringify(payload), MsgSource.Web, MsgTypes.NativeShare)
    const reply = await sendAndAwaitReply(mobileEventBus$, message, {
        timeout: options.timeoutMs ?? 60000,
        retries: 0,
    })

    const resultStr = reply.content || reply.responsePayload || '{}'
    const result: NativeShareResult = JSON.parse(resultStr)

    if (result.error) {
        throw new Error(result.error)
    }

    return result
}

export async function shareTextOrUrlNative(
    payload: NativeSharePayload,
    options: { timeoutMs?: number } = {},
): Promise<NativeShareResult | null> {
    if (!hasNativeCapability('nativeShare')) return null

    return sendNativeShare(payload, options)
}

export async function shareFileUrlNative({
    fileUrl,
    fileName,
    mimeType,
    title,
    text,
}: {
    fileUrl: string
    fileName: string
    mimeType: string
    title?: string
    text?: string
}): Promise<NativeShareResult | null> {
    if (!hasNativeCapability('nativeShareUrl')) return null

    return sendNativeShare({ fileUrl, fileName, mimeType, title, text }, { timeoutMs: 120000 })
}

const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
    const chunkSize = 0x8000
    let binary = ''

    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize)
        binary += String.fromCharCode(...chunk)
    }

    return btoa(binary)
}

export async function shareFileNative({
    blob,
    fileName,
    mimeType,
    title,
    text,
}: {
    blob: Blob
    fileName: string
    mimeType?: string
    title?: string
    text?: string
}): Promise<NativeShareResult | null> {
    if (!hasNativeCapability('nativeShare')) return null

    const arrayBuffer = await blob.arrayBuffer()
    const fileBase64 = uint8ArrayToBase64(new Uint8Array(arrayBuffer))

    return shareTextOrUrlNative(
        {
            title,
            text,
            fileBase64,
            fileName,
            mimeType: mimeType || blob.type || 'application/octet-stream',
        },
        { timeoutMs: 120000 },
    )
}
